import { CheckIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@plato/app-core/hooks/use-user';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { api } from '../../../../utils/api.ts';
import { getFullName } from '../../../../utils/name.ts';

import { SearchCombobox } from '@plato/ui-legacy/components/search-combobox/search-combobox';
import { cn } from '@plato/ui/util/classes';

type UsersSelectProps = { className?: string; error?: boolean } & (
    | { multiple?: false; value: string; onChange: (value: string) => void }
    | { multiple: true; value: string[]; onChange: (value: string[]) => void }
);

type User = {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    deletedAt?: Date | null;
};

export function UsersSelect({ className, error = false, ...props }: UsersSelectProps) {
    const { t } = useTranslation();
    const currentUserId = useUser().id;
    const { data: platoUsers, isLoading } = api.platoUser.query.users.getManyUsers.getList.useQuery();
    const [searchQuery, setSearchQuery] = React.useState('');
    const [open, setOpen] = React.useState(false);

    const users = React.useMemo(() => {
        if (platoUsers) {
            return platoUsers.map((user: Omit<User, 'fullName'>) => ({
                ...user,
                fullName: getFullName(user.firstName, user.lastName),
            }));
        }
        return [];
    }, [platoUsers]);

    const cleanFilteredValues = useMemo(
        () =>
            users
                .filter(
                    (user) =>
                        !user.deletedAt && user.fullName.toLowerCase().includes(searchQuery.toLowerCase()),
                )
                .toSorted((a, b) => {
                    if (a.id === currentUserId) {
                        return -1;
                    }
                    if (b.id === currentUserId) {
                        return 1;
                    }
                    return 0;
                }),
        [users, searchQuery, currentUserId],
    );

    const selectedIds = useMemo(() => {
        if (props.multiple) {
            return props.value;
        }
        return props.value ? [props.value] : [];
    }, [props.multiple, props.value]);

    const selectedUsers = useMemo(
        () => users.filter((user: User) => selectedIds.includes(user.id)),
        [users, selectedIds],
    );

    const handleChange = (id: string): void => {
        if (props.multiple) {
            props.onChange(
                props.value.includes(id) ? props.value.filter((value) => value !== id) : [...props.value, id],
            );
            return;
        }

        props.onChange(id);
    };

    return (
        <SearchCombobox<User>
            selectedValue={props.multiple ? '' : selectedIds[0] || ''}
            closeOnSelect={!props.multiple}
            isLoading={isLoading}
            groupedItems={[
                {
                    id: 'users',
                    items: cleanFilteredValues,
                },
            ]}
            keySelector={(user) => user.id}
            valueSelector={(user) => user.id}
            renderItem={(user) => (
                <div className="flex w-full items-center justify-between gap-cozy">
                    <span>{user.fullName}</span>
                    {props.multiple && selectedIds.includes(user.id) && <CheckIcon className="h-4 w-4" />}
                </div>
            )}
            onSearchQueryChange={setSearchQuery}
            searchQuery={searchQuery}
            onValueChange={handleChange}
            open={open}
            setOpen={setOpen}
            notFoundPlaceholder={t('no_data')}
            inputPlaceholder={t('search')}
            className={cn('min-w-0', className)}
        >
            <Button
                variant="ghost"
                className={cn(
                    'max-w-full font-normal',
                    error && 'border border-red-500 hover:border-red-600',
                    className,
                )}
                aria-invalid={error || undefined}
                type="button"
                onClick={() => {
                    setOpen((prev) => !prev);
                }}
                disabled={isLoading}
            >
                {selectedUsers.length > 0 ? (
                    <span className="truncate">
                        {selectedUsers.map((user: User) => user.fullName).join(', ')}
                    </span>
                ) : props.multiple ? (
                    <span className="truncate">
                        {t('tasks__assigned_to_placeholder_multiple', {
                            ns: 'tasks',
                        })}
                    </span>
                ) : (
                    t('tasks__assigned_to_placeholder', {
                        ns: 'tasks',
                    })
                )}
            </Button>
        </SearchCombobox>
    );
}
